// @ts-ignore
import _ from 'lodash';
import { ProcessSupervisionChunk } from './types';
import { ProcessChunkingStrategyWithParams } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/chunking';
import { unchunk } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/unchunking';
import { StepParamsForType, InteractionStepType } from '@scale/shared/types/chat';

const levenshtein = (a: string, b: string): number => {
  const matrix = Array.from({ length: a.length }).map(() =>
    Array.from({ length: b.length }).map(() => 0),
  );

  for (let i = 0; i < a.length; i++) matrix[i][0] = i + 1;

  for (let i = 0; i < b.length; i++) matrix[0][i] = i + 1;

  for (let j = 0; j < b.length; j++)
    for (let i = 0; i < a.length; i++)
      matrix[i][j] = Math.min(
        (i === 0 ? 0 : matrix[i - 1][j]) + 1,
        (j === 0 ? 0 : matrix[i][j - 1]) + 1,
        (i === 0 || j === 0 ? 0 : matrix[i - 1][j - 1]) + (a[i] === b[j] ? 0 : 1),
      );

  // adding the diff between lenghts gives a fundamentally better result
  return matrix[a.length - 1][b.length - 1] + Math.abs(a.length - b.length);
};

export const getShouldResetHistory = (
  response: any,
  responseToSubmit: any,
  shouldContinue: boolean | undefined,
  reRequestModel: boolean | undefined,
  endAfterRatingIds: (number | undefined)[] | undefined,
): boolean => {
  if (!reRequestModel) {
    return false;
  }
  // We only want to reset history if the ratings have changed
  // in their semantic meaning. We don't want to reset if the reason
  // it was incorrect is different.
  const ratingsToValidity = (rating: number | undefined) =>
    rating === undefined ? undefined : endAfterRatingIds?.includes(rating);

  const newRatings = responseToSubmit?.context?.chunks.map((chunk: ProcessSupervisionChunk) =>
    ratingsToValidity(chunk.rating),
  );
  const oldRatings = response?.context?.chunks.map((chunk: ProcessSupervisionChunk) =>
    ratingsToValidity(chunk.rating),
  );
  const ratingsChanged = !_.isEqual(newRatings, oldRatings);

  const newRewrites = responseToSubmit?.context?.chunks.map(
    (chunk: ProcessSupervisionChunk) => chunk.rewrite,
  );
  const oldRewrites = response?.context?.chunks.map(
    (chunk: ProcessSupervisionChunk) => chunk.rewrite,
  );
  const rewritesChanged = !_.isEqual(newRewrites, oldRewrites);

  const newReflections = responseToSubmit?.context?.chunks.map(
    (chunk: ProcessSupervisionChunk) => chunk.reflection,
  );
  const oldReflections = response?.context?.chunks.map(
    (chunk: ProcessSupervisionChunk) => chunk.reflection,
  );
  const reflectionsChanged = !_.isEqual(newReflections, oldReflections);

  const newIsQuestionSolvedByRewrite = responseToSubmit?.context?.isQuestionSolvedByRewrite;
  const oldIsQuestionSolvedByRewrite = response?.context?.isQuestionSolvedByRewrite;
  const isQuestionSolvedByRewriteChanged =
    newIsQuestionSolvedByRewrite !== oldIsQuestionSolvedByRewrite;

  const shouldResetHistory =
    (ratingsChanged ||
      (shouldContinue && rewritesChanged) ||
      (shouldContinue && reflectionsChanged) ||
      isQuestionSolvedByRewriteChanged) ??
    false;

  return shouldResetHistory;
};

export const getExperimentalToolUseShouldResetHistory = (
  response: any,
  responseToSubmit: any,
  shouldContinue: boolean | undefined,
  reRequestModel: boolean | undefined,
): boolean => {
  if (!reRequestModel) {
    return false;
  }
  const newRatings = responseToSubmit?.context?.chunks.map(
    (chunk: ProcessSupervisionChunk) => chunk.rating,
  );
  const oldRatings = response?.context?.chunks.map(
    (chunk: ProcessSupervisionChunk) => chunk.rating,
  );
  const ratingsChanged = !_.isEqual(newRatings, oldRatings);

  const newIsQuestionSolvedByRewrite = responseToSubmit?.context?.isQuestionSolvedByRewrite;
  const oldIsQuestionSolvedByRewrite = response?.context?.isQuestionSolvedByRewrite;
  const isQuestionSolvedByRewriteChanged =
    newIsQuestionSolvedByRewrite !== oldIsQuestionSolvedByRewrite;

  const shouldResetHistory =
    (ratingsChanged || shouldContinue || isQuestionSolvedByRewriteChanged) ?? false;

  return shouldResetHistory;
};

export const getIsComplete = (
  processSteps: ProcessSupervisionChunk[],
  ratings: { [key: string]: any },
  isQuestionSolvedByRewrite: boolean | undefined,
  endAfterRatingIds: number[] | undefined,
): boolean =>
  (processSteps.length === Object.keys(ratings).length &&
    _.every(Object.values(ratings), (rating: any) => !_.isUndefined(rating)) &&
    _.every(Object.values(ratings), (rating: any) =>
      endAfterRatingIds ? !endAfterRatingIds.includes(rating) : rating !== -1,
    )) ||
  isQuestionSolvedByRewrite === true;

export const getStepOutput = (
  processSteps: ProcessSupervisionChunk[],
  chunkStrategyWithParams: ProcessChunkingStrategyWithParams,
  firstBadRatingIndex: number,
  { params }: { params: StepParamsForType<InteractionStepType.ProcessSupervision>['params'] },
  rewriteFieldId: string = 'rewrite',
  rewriteRatingIds: number[],
): string => {
  const showOnlyFirstBadRating = params.output_first_bad_rating_only;
  const removeRegexMatches = params.remove_regex_matches;
  // Apply regex removal if specified
  if (removeRegexMatches) {
    processSteps = processSteps.map((chunk: ProcessSupervisionChunk) => {
      const updatedChunk = { ...chunk };
      if (chunk?.content) {
        updatedChunk.content = chunk.content.replace(new RegExp(removeRegexMatches, 'g'), '');
      }
      if (chunk[rewriteFieldId]) {
        updatedChunk[rewriteFieldId] = chunk[rewriteFieldId].replace(
          new RegExp(removeRegexMatches, 'g'),
          '',
        );
      }
      return updatedChunk;
    });
  }

  // Return only the first bad rating content if specified
  if (showOnlyFirstBadRating) {
    return processSteps[firstBadRatingIndex]?.content || '';
  }

  // Otherwise, return the processed content up to the first bad rating index
  const postprocessedChunks = processSteps
    .slice(0, firstBadRatingIndex + 1)
    .map((chunk: ProcessSupervisionChunk) => {
      const getContent = () => {
        if (!_.isNil(chunk?.rating) && rewriteRatingIds.includes(chunk.rating)) {
          return chunk.rewrite;
        }

        return chunk.content || '';
      };
      const content = getContent();
      const reflection = chunk.reflection ? '\n\n' + chunk.reflection : '';
      return content + reflection;
    });
  const { output } = unchunk(postprocessedChunks, chunkStrategyWithParams);
  return output;
};

export const getShouldSubmit = (
  complete: boolean | undefined,
  ratings: { [key: string]: any } | undefined,
  processSteps: ProcessSupervisionChunk[],
): boolean => {
  // if they are already done and are editing stuff, then submit
  if (complete) {
    return true;
  }

  if (!ratings) {
    return false;
  }

  if (!Object.keys(ratings).length || !processSteps.length) {
    return false;
  }
  return true;
};

export const experimentalToolUseIsComplete = (
  processSteps: ProcessSupervisionChunk[],
  isQuestionSolvedByRewrite: boolean | undefined,
): boolean => {
  if (isQuestionSolvedByRewrite) {
    return true;
  }

  if (processSteps.length >= 4) {
    const isFinishAction = processSteps[processSteps.length - 3].rewrite
      ? processSteps[processSteps.length - 3].rewrite?.includes('finish') || false
      : processSteps[processSteps.length - 3].content.includes('finish');
    const isFinishActionInput = processSteps[processSteps.length - 2].rewrite
      ? processSteps[processSteps.length - 2].rewrite?.includes('answer') || false
      : processSteps[processSteps.length - 2].content.includes('answer');
    return isFinishAction && isFinishActionInput;
  } else {
    return false;
  }
};

export const experimentalGetStepOutput = (
  actionPlanChunk: ProcessSupervisionChunk | undefined,
  chunkStrategyWithParams: ProcessChunkingStrategyWithParams,
  processSteps: ProcessSupervisionChunk[],
  firstBadRatingIndex: number,
  previousCorrectedStepsLength: number,
): string => {
  const actionPlanString = actionPlanChunk?.rewrite || actionPlanChunk?.content || '';

  const postprocessedChunks = processSteps
    .slice(0, Math.min(firstBadRatingIndex - 1, processSteps.length - 1))
    .map((chunk: ProcessSupervisionChunk) =>
      chunk.rating === -1 && chunk?.rewrite ? chunk?.rewrite : chunk?.content || '',
    );

  const { output: stepsString } = unchunk(postprocessedChunks, chunkStrategyWithParams);

  if (previousCorrectedStepsLength > 0) {
    return stepsString;
  } else if (actionPlanChunk?.rating === -1) {
    return actionPlanString;
  } else {
    return `${actionPlanString}\n\n${stepsString}\n`;
  }
};
export const validateStepContentMatches = (
  processChunks: { content: string }[],
  originalContent: string | undefined,
  ignoreTerm: RegExp | undefined = /\s+/g,
  maxLevenshteinDistance: number = 0,
): { success: true } | { success: false; error: { message: string } } => {
  if (!originalContent) {
    return {
      success: false,
      error: {
        message: 'Original content is undefined.',
      },
    };
  }

  const normalizeContent = (content: string) => content.replace(ignoreTerm, '').trim();

  const normalizedOriginalContent = normalizeContent(originalContent);
  const normalizedChunksContent = processChunks
    .map(chunk => normalizeContent(chunk.content))
    .join('');

  if (normalizedChunksContent.length === 0) {
    return {
      success: false,
      error: {
        message: 'Process chunks are empty.',
      },
    };
  }

  const distance = levenshtein(normalizedOriginalContent, normalizedChunksContent);

  if (distance > maxLevenshteinDistance) {
    return {
      success: false,
      error: {
        message: `The content of the chunks does not match the original content within the allowed Levenshtein distance (${distance} > ${maxLevenshteinDistance}). Please ensure that your chunks contain similar content to the original.`,
      },
    };
  }

  return { success: true };
};

export const replaceTemplateVariables = (
  text: string | undefined,
  variables: Record<string, string> | undefined,
) => {
  if (!text || !variables) return text;
  const variableMatch = text.match(/{{(.*?)}}/);
  if (variableMatch) {
    const variableName = variableMatch[1].trim();
    const variableValue = variables[variableName];
    if (variableValue) {
      return text.replace(/{{(.*?)}}/g, variableValue);
    }
  }
  return text;
};
